import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  styled,
} from '@mui/material';
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useUser } from '../../../contexts/UserContext';
import { formatDateTimeUTC } from '../../../utils/timeUtils';
import Text from '../../elements/text/Text';
import NoContent from '../../sections/no-content/NoContent';

const statusColor = (monitor, status, theme) => {
  return monitor
    ? status === 'OK'
      ? theme.palette.success.main
      : theme.palette.error.main
    : theme.palette.grey[500];
};

const StyledWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    display: 'block',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    marginTop: '1em',
  },
}));

const StyledStatusIcon = styled(FiberManualRecordIcon)(({
  monitor,
  status,
  theme,
}) => {
  const monitorStatus = statusColor(monitor, status, theme);
  return {
    fontSize: '1em',
    marginLeft: '1em',
    color: monitorStatus,
  };
});

const StyledStatusSpan = styled('span')(({ monitor, status, theme }) => {
  const monitorStatus = statusColor(monitor, status, theme);
  return {
    fontWeight: 'bold',
    marginRight: theme.spacing(4),
    color: monitorStatus,
  };
});

const StyledNoStatusSpan = styled('span')({
  color: 'black',
  fontWeight: 'bold',
  marginRight: '2em',
});

const InstanceStatus = ({ monitor, status, onStatusCheck }) => {
  const user = useUser();
  const { t } = useTranslation();

  const handleStatusCheck = () => onStatusCheck();

  return (
    <>
      <StyledWrapper>
        <Box sx={{ mr: 2 }}>{t('monitorStatus', { ns: 'instance' })}</Box>
        {status ? (
          <>
            <StyledStatusSpan monitor={monitor} status={status?.status}>
              {status?.status}
            </StyledStatusSpan>
            <Box sx={{ mr: 4 }}>
              (
              {t('updated', {
                ns: 'instance',
                dateTime: formatDateTimeUTC(status.checkDate, user.timeZone),
              })}
              )
            </Box>
          </>
        ) : (
          <StyledNoStatusSpan>NO STATUS</StyledNoStatusSpan>
        )}

        <StyledButton onClick={handleStatusCheck} disabled={!status}>
          {t('checkNow', { ns: 'buttons' })}
        </StyledButton>
      </StyledWrapper>

      <Box sx={{ mt: 1 }}>
        {status ? (
          status.nodes.map((node, i) => (
            <Accordion key={i}>
              <AccordionSummary
                style={{ display: 'flex', alignItems: 'center' }}
              >
                {t('monitorNode', { ns: 'instance', node: node.name })}
                <StyledStatusIcon monitor={monitor} status={node.status} />
              </AccordionSummary>
              <AccordionDetails style={{ display: 'block' }}>
                {node.status === 'ERROR' && (
                  <Text bodyBold>{node.message}</Text>
                )}
                {node.collections.map((collection, i) => (
                  <Accordion key={i} style={{ width: '100%' }}>
                    <AccordionSummary
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      {t('monitorCollection', {
                        ns: 'instance',
                        collection: collection.name,
                      })}
                      <StyledStatusIcon
                        monitor={monitor}
                        status={collection.status}
                      />
                    </AccordionSummary>
                    <AccordionDetails>{collection.message}</AccordionDetails>
                  </Accordion>
                ))}
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          <NoContent title={t('noHealthCheckFound', { ns: 'descriptions' })} />
        )}
      </Box>
    </>
  );
};

InstanceStatus.propTypes = {
  monitor: PropTypes.bool,
  status: PropTypes.object,
  onStatusCheck: PropTypes.func.isRequired,
};

export default InstanceStatus;
